/* ==============================================================================
 * prix-conf-client.js
 * Helper unique : prix + variantId d'une combinaison (couleur, caisson, poignee)
 * via Shopify Storefront API > product.variantBySelectedOptions.
 *
 * Couvre le blocage des >250 variants : Storefront API expose TOUS les variants,
 * pas seulement les 250 premiers du JSON public.
 *
 * Source unique : Shopify lui-meme. Pas de metafield cache, pas d'etalon ratio.
 *
 * API publique :
 *   window.mooodPrixConf.prixConf(produit, opts?)      -> Promise<{priceCents, variantId} | null>
 *   window.mooodPrixConf.prixConfBatch(produits, opts?) -> Promise<Map<handle, {priceCents,variantId}|null>>
 *
 * Cache en memoire : 15 min, par cle (handle, dimCaisson, couleur, poignee).
 * Dedup des fetchs concurrents sur la meme cle.
 * ============================================================================ */

(function() {
  'use strict';

  var SF_ENDPOINT_PATH = '/api/2025-01/graphql.json';
  var CACHE_TTL_MS = 15 * 60 * 1000;

  var RE_COULEUR = /couleur|color/i;
  var RE_POIGNEE = /poign/i;
  var RE_DIM_NAME = /largeur|hauteur|taille|caisson/i;

  // Storefront endpoint : derive du shop.permanent_domain injecte par Shopify
  // Liquid (Shopify.shop), fallback hardcode si manquant. Le custom domain
  // (moood.fr) peut aussi servir mais permanent_domain est plus fiable cross-tunnel.
  function getEndpoint() {
    var shop = (window.Shopify && window.Shopify.shop) || 'cfsuni-in.myshopify.com';
    return 'https://' + shop + SF_ENDPOINT_PATH;
  }

  function getToken() {
    return window.MOOOD_SF_TOKEN || null;
  }

  // ===== state access ============================================================
  // Le configurateur expose son state via window.mooodState (defini dans
  // configurateur-moood.js apres modif). On lit caissonCouleur/caissonColorPrecise/
  // couleurActive/poigneeActive/poigneeBasColonne/poigneeHaut directement.
  function getState() {
    return window.mooodState || {};
  }

  // ===== option detection =======================================================
  function getOptName(o) {
    return typeof o === 'string' ? o : (o && o.name) || '';
  }

  function findOptionIdxByName(produit, regex) {
    var opts = (produit && produit.options) || [];
    for (var i = 0; i < opts.length; i++) {
      if (regex.test(getOptName(opts[i]))) return i;
    }
    return -1;
  }

  // Detection dim/caisson : value-first (variant[0] dont la value contient
  // "caisson"), fallback name-based. Couvre meubles bas/hauts (option name
  // "Largeur caisson") ET colonnes (option name "Taille") -- meme logique que
  // dans le mass regen serveur.
  function findDimCaissonIdx(produit) {
    // Etape C : prefere _firstVariantOpts (mode SF : variants vide). Le scan
    // variants[0] reste en fallback pour les produits hors helper (legacy).
    if (produit && produit._firstVariantOpts && produit.options) {
      for (var k = 0; k < produit._firstVariantOpts.length; k++) {
        var so = produit._firstVariantOpts[k];
        if (typeof so.value === 'string' && /caisson/i.test(so.value)) {
          for (var j = 0; j < produit.options.length; j++) {
            if (produit.options[j].name === so.name) return j;
          }
        }
      }
    }
    var v0 = produit && produit.variants && produit.variants[0];
    if (v0) {
      for (var i = 1; i <= 3; i++) {
        var val = v0['option' + i];
        if (typeof val === 'string' && /caisson/i.test(val)) return i - 1;
      }
    }
    return findOptionIdxByName(produit, RE_DIM_NAME);
  }

  function caissonValFromState(state) {
    if (state.caissonColorPrecise) return 'Caisson couleur au choix';
    var map = { 'Blanc': 'Caisson blanc', 'Gris anthracite': 'Caisson gris foncé', 'Bois': 'Caisson couleur au choix', 'Couleur au choix': 'Caisson couleur au choix' }; // 2026-10-23 : « Couleur au choix » sans teinte précise était chiffré en caisson blanc
    return map[state.caissonCouleur] || 'Caisson blanc';
  }

  // Pour 1 produit : derive la valeur "<dim> - <caisson>" qui correspond a
  // selectedOptions[axe dim/caisson].
  function computeDimCaissonValue(produit, state) {
    var dimIdx = findDimCaissonIdx(produit);
    if (dimIdx < 0) return null;
    var baseValue = null;
    // Etape C : mode SF (variants vide) -> lecture _firstVariantOpts par name.
    if (produit && produit._firstVariantOpts && produit.options) {
      var dimOptName = produit.options[dimIdx] && produit.options[dimIdx].name;
      if (dimOptName) {
        for (var i = 0; i < produit._firstVariantOpts.length; i++) {
          if (produit._firstVariantOpts[i].name === dimOptName) {
            baseValue = produit._firstVariantOpts[i].value;
            break;
          }
        }
      }
    }
    // Fallback legacy : scan variants[0]['option' + ...]
    if (typeof baseValue !== 'string') {
      var v0 = produit && produit.variants && produit.variants[0];
      if (v0) baseValue = v0['option' + (dimIdx + 1)];
    }
    if (typeof baseValue !== 'string') return null;
    var m = baseValue.match(/^(.+?) - Caisson/i);
    if (!m) return null;
    return m[1] + ' - ' + caissonValFromState(state);
  }

  // Poignee : depend de la collection. Les meubles haut utilisent poigneeHaut,
  // les autres poigneeBasColonne. Si poignee specifique non set, fallback sur
  // poigneeActive.
  function poigneeForProduit(produit, state, collectionHandle) {
    // Si caller fournit explicitement, on l'utilise (le configurateur sait
    // mieux que nous via getPoigneeForCollection)
    if (state._poigneeOverride) return state._poigneeOverride;
    // Detection collection : si on a pas le handle, on tente de derive depuis
    // produit.tags ou on tombe sur poigneeActive
    var isHaut = collectionHandle === 'meuble-haut-unit';
    if (isHaut) return state.poigneeHaut || state.poigneeActive || null;
    return state.poigneeBasColonne || state.poigneeActive || null;
  }

  // ===== build selectedOptions ===================================================
  function buildSelectedOptions(produit, state, opts) {
    opts = opts || {};
    if (!produit || !produit.handle) return null;

    var couleur = opts.couleur || (state.couleurActive && state.couleurActive.name);
    if (!couleur) return null;

    var optsList = (produit.options) || [];
    var dimIdx = findDimCaissonIdx(produit);
    var couleurIdx = findOptionIdxByName(produit, RE_COULEUR);
    var poigneeIdx = findOptionIdxByName(produit, RE_POIGNEE);
    if (couleurIdx < 0) return null;

    // Produits COULEUR-SEULE (joues, fileurs, facades lave-vaisselle) : une
    // seule option "Couleur", pas de dim caisson ni de poignee. Le variant se
    // resout par la couleur uniquement -> selectedOptions = [{Couleur}].
    // (Avant : ces produits ne passaient pas ici -> charges via variants(first:30)
    //  qui ne couvrait pas les 137 couleurs -> cartes grisees pour couleur >30e.)
    if (dimIdx < 0 && poigneeIdx < 0) {
      return {
        selectedOptions: [{ name: getOptName(optsList[couleurIdx]), value: couleur }],
        cacheKey: produit.handle + '|' + couleur
      };
    }

    var dimCaissonValue = computeDimCaissonValue(produit, state);
    if (!dimCaissonValue) return null;

    var poigneeVal = opts.poignee || poigneeForProduit(produit, state, opts.collection);
    if (!poigneeVal) return null;

    if (dimIdx < 0 || couleurIdx < 0 || poigneeIdx < 0) return null;

    var dimName = getOptName(optsList[dimIdx]);
    var couleurName = getOptName(optsList[couleurIdx]);
    var poigneeName = getOptName(optsList[poigneeIdx]);

    var selectedOptions = [
      { name: dimName, value: dimCaissonValue },
      { name: couleurName, value: couleur },
      { name: poigneeName, value: poigneeVal }
    ];
    var cacheKey = produit.handle + '|' + dimCaissonValue + '|' + couleur + '|' + poigneeVal;
    return { selectedOptions: selectedOptions, cacheKey: cacheKey };
  }

  // ===== cache ==================================================================
  var _cache = new Map();
  var _inflight = new Map();

  function readCache(key) {
    var e = _cache.get(key);
    if (!e) return null;
    if (Date.now() > e.expiresAt) {
      _cache.delete(key);
      return null;
    }
    return { priceCents: e.priceCents, variantId: e.variantId, imageUrl: e.imageUrl || null };
  }

  function writeCache(key, value) {
    _cache.set(key, {
      priceCents: value.priceCents,
      variantId: value.variantId,
      imageUrl: value.imageUrl || null,
      expiresAt: Date.now() + CACHE_TTL_MS
    });
  }

  // ===== Storefront GraphQL =====================================================
  // Retry avec backoff : la Storefront API throttle (429/430) et renvoie des
  // 500/INTERNAL_SERVER_ERROR transitoires en charge (plusieurs collections
  // lourdes + batchs prix enchaines). Sans retry, un seul echec fait tomber
  // loadCollectionData sur le fallback products.json (20 produits, SANS
  // metafields -> plus aucun filtre). Les requetes sont des reads GraphQL
  // idempotents, donc rejouables sans risque.
  var SF_RETRY_STATUS = { 429: 1, 430: 1, 500: 1, 502: 1, 503: 1, 504: 1 };
  var SF_MAX_ATTEMPTS = 4;
  var SF_BACKOFF_MS = [700, 1800, 3500]; // avant tentative 2, 3, 4

  function _sfDelay(ms) { return new Promise(function(r) { setTimeout(r, ms); }); }

  function _isThrottleError(json) {
    if (!json || !json.errors || !json.errors.length) return false;
    var s = JSON.stringify(json.errors).toUpperCase();
    return s.indexOf('THROTTLED') !== -1 || s.indexOf('INTERNAL_SERVER_ERROR') !== -1;
  }

  function sfQuery(query, variables) {
    var token = getToken();
    if (!token) return Promise.reject(new Error('MOOOD_SF_TOKEN non defini (theme setting)'));

    return (function attempt(n) {
      return fetch(getEndpoint(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token,
          'Accept': 'application/json'
        },
        body: JSON.stringify({ query: query, variables: variables || {} })
      }).then(function(res) {
        if (!res.ok) {
          if (SF_RETRY_STATUS[res.status] && n < SF_MAX_ATTEMPTS) {
            return _sfDelay(SF_BACKOFF_MS[n - 1]).then(function() { return attempt(n + 1); });
          }
          return res.text().then(function(t) { throw new Error('Storefront HTTP ' + res.status + ': ' + t.slice(0, 200)); });
        }
        return res.json().then(function(json) {
          if (json && json.errors && json.errors.length) {
            if (_isThrottleError(json) && n < SF_MAX_ATTEMPTS) {
              return _sfDelay(SF_BACKOFF_MS[n - 1]).then(function() { return attempt(n + 1); });
            }
            throw new Error('Storefront errors: ' + JSON.stringify(json.errors).slice(0, 300));
          }
          return json.data;
        });
      }, function(networkErr) {
        // Erreur reseau (fetch rejette) : rejouable aussi
        if (n < SF_MAX_ATTEMPTS) {
          return _sfDelay(SF_BACKOFF_MS[n - 1]).then(function() { return attempt(n + 1); });
        }
        throw networkErr;
      });
    })(1);
  }

  function gidToNumeric(gid) {
    if (typeof gid !== 'string') return null;
    var m = gid.match(/(\d+)$/);
    return m ? parseInt(m[1], 10) : null;
  }

  // ===== public API : single ====================================================
  function prixConf(produit, opts) {
    opts = opts || {};
    var state = getState();
    if (opts.poignee) state = Object.assign({}, state, { _poigneeOverride: opts.poignee });
    var built = buildSelectedOptions(produit, state, opts);
    if (!built) return Promise.resolve(null);

    var cached = readCache(built.cacheKey);
    if (cached) return Promise.resolve(cached);

    if (_inflight.has(built.cacheKey)) return _inflight.get(built.cacheKey);

    var p = sfQuery(
      'query VarByOpt($handle:String!,$opts:[SelectedOptionInput!]!){'
      + 'product(handle:$handle){variantBySelectedOptions(selectedOptions:$opts){id price{amount} image{url}}}'
      + '}',
      { handle: produit.handle, opts: built.selectedOptions }
    ).then(function(data) {
      var v = data && data.product && data.product.variantBySelectedOptions;
      if (!v) return null;
      var result = {
        priceCents: Math.round(parseFloat(v.price.amount) * 100),
        variantId: gidToNumeric(v.id),
        imageUrl: (v.image && v.image.url) || null
      };
      writeCache(built.cacheKey, result);
      return result;
    }).catch(function(err) {
      console.warn('[prix-conf] prixConf failed for', produit.handle, ':', err.message);
      return null;
    }).then(function(r) {
      _inflight.delete(built.cacheKey);
      return r;
    });

    _inflight.set(built.cacheKey, p);
    return p;
  }

  // ===== public API : batched ===================================================
  // 1 round-trip Storefront pour N produits via aliases GraphQL.
  function prixConfBatch(produits, opts) {
    opts = opts || {};
    var state = getState();
    if (opts.poignee) state = Object.assign({}, state, { _poigneeOverride: opts.poignee });

    var items = [];
    for (var i = 0; i < produits.length; i++) {
      var produit = produits[i];
      var built = buildSelectedOptions(produit, state, opts);
      if (!built) { items.push({ produit: produit, built: null, result: null }); continue; }
      var cached = readCache(built.cacheKey);
      if (cached) { items.push({ produit: produit, built: built, result: cached }); continue; }
      items.push({ produit: produit, built: built, result: null });
    }

    var toFetch = items.filter(function(it) { return it.built && !it.result; });
    var fetchPromise = Promise.resolve();

    if (toFetch.length > 0) {
      // Chunk en blocs de 30 pour eviter d'enormes queries
      var chunks = [];
      for (var c = 0; c < toFetch.length; c += 30) chunks.push(toFetch.slice(c, c + 30));

      fetchPromise = chunks.reduce(function(prev, chunk) {
        return prev.then(function() {
          var varDecls = chunk.map(function(_, idx) { return '$opts' + idx + ':[SelectedOptionInput!]!'; }).join(',');
          var aliases = chunk.map(function(it, idx) {
            // Escape double quotes dans handle (paranoiac)
            var safeHandle = String(it.produit.handle).replace(/[\\"]/g, '\\$&');
            return 'p' + idx + ':product(handle:"' + safeHandle + '"){variantBySelectedOptions(selectedOptions:$opts' + idx + '){id price{amount} image{url}}}';
          }).join(' ');
          var query = 'query Batch(' + varDecls + '){' + aliases + '}';
          var variables = {};
          chunk.forEach(function(it, idx) { variables['opts' + idx] = it.built.selectedOptions; });

          return sfQuery(query, variables).then(function(data) {
            chunk.forEach(function(it, idx) {
              var v = data && data['p' + idx] && data['p' + idx].variantBySelectedOptions;
              if (v) {
                var result = {
                  priceCents: Math.round(parseFloat(v.price.amount) * 100),
                  variantId: gidToNumeric(v.id),
                  imageUrl: (v.image && v.image.url) || null
                };
                writeCache(it.built.cacheKey, result);
                it.result = result;
              }
            });
          }).catch(function(err) {
            console.warn('[prix-conf] batch failed:', err.message);
          });
        });
      }, Promise.resolve());
    }

    return fetchPromise.then(function() {
      var out = new Map();
      for (var i = 0; i < items.length; i++) {
        out.set(items[i].produit.handle, items[i].result);
      }
      return out;
    });
  }

  // ===== listProductsByCollection ===============================================
  // Refacto chargement produits du configurateur (Etape A) :
  // remplace /collections/<h>/products.json (~13 MB par collection) par une
  // query Storefront GraphQL ciblee qui ne demande QUE les champs utilises
  // (~65 KB par collection). Tous metafields utilises sont remontes en 1 query.
  //
  // Retour : { products: [...shape ~ products.json...], metafields: { [id]: {...} } }
  //   products[i].variants = []  (intentionnellement vide, les variants sont
  //     resolus a la demande par prixConf/variantBySelectedOptions).
  //   products[i].featured_image = { src, alt }  (shape products.json)
  //   metafields[productId] = forme compatible window.mooodMetafields existante.
  //
  // Cache memoire 15 min par handle (cf. CACHE_TTL_MS). Hit/miss expose dans
  // _listCache pour debug. dedup des fetchs concurrents via _listInflight.

  var _listCache = new Map();    // handle -> { products, metafields, expiresAt }
  var _listInflight = new Map(); // handle -> Promise

  function readListCache(handle) {
    var e = _listCache.get(handle);
    if (!e) return null;
    if (Date.now() > e.expiresAt) { _listCache.delete(handle); return null; }
    return { products: e.products, metafields: e.metafields };
  }

  function writeListCache(handle, data) {
    _listCache.set(handle, {
      products: data.products,
      metafields: data.metafields,
      expiresAt: Date.now() + CACHE_TTL_MS
    });
  }

  // Parse les metafields type "list.single_line_text_field" (Storefront retourne
  // le value en string JSON) ou single_line_text_field (retourne le string brut).
  // window.mooodMetafields stocke historiquement des array, on aligne.
  function parseMetafieldValue(value) {
    if (value === null || value === undefined) return null;
    if (typeof value !== 'string') return value;
    if (value.charAt(0) === '[' || value.charAt(0) === '{') {
      try {
        return JSON.parse(value);
      } catch (e) { /* fallthrough */ }
    }
    return value;
  }

  // Base : tous les champs sans variants (collections grosses : meuble-bas/haut/colonne).
  // Les variants sont resolus a la demande via prixConf/variantBySelectedOptions.
  var LIST_QUERY_NO_VARIANTS = (
    'query ListConfigProducts($h:String!,$n:Int!,$after:String){'
    + 'collection(handle:$h){'
      + 'products(first:$n,after:$after){'
        + 'pageInfo{hasNextPage endCursor}'
        + 'edges{node{'
          + 'id handle title tags '
          + 'featuredImage{url altText} '
          + 'options{name} '
          + 'priceRange{minVariantPrice{amount currencyCode}maxVariantPrice{amount currencyCode}} '
          + 'firstVariant:variants(first:1){edges{node{selectedOptions{name value}}}} '
          + 'largeur:metafield(namespace:"custom",key:"largeur_de_caisson"){value} '
          + 'hauteur:metafield(namespace:"custom",key:"hauteur_de_caisson"){value} '
          + 'profondeur:metafield(namespace:"custom",key:"profondeur_de_caisson"){value} '
          + 'type_meuble:metafield(namespace:"custom",key:"type_de_meuble"){value} '
          + 'long_poig:metafield(namespace:"custom",key:"longueur_des_poignees"){value} '
          + 'mat_poig:metafield(namespace:"custom",key:"matiere_des_poignees"){value} '
          + 'joue:metafield(namespace:"custom",key:"joue_et_fileur"){value} '
          + 'electro:metafield(namespace:"custom",key:"type_d_electro"){value} '
          + 'badge:metafield(namespace:"custom",key:"custom_product_badge"){value}'
        + '}}'
      + '}'
    + '}'
    + '}'
  );

  // Variante : avec variants(first:30) pour les collections "simples" (poignee,
  // joues, electro). variantId reel necessaire au panier (pas de prixConfBatch
  // qui resoudrait pour elles). +~150 bytes/produit, ok pour ces collections
  // generalement petites (<30 produits).
  var LIST_QUERY_WITH_VARIANTS = LIST_QUERY_NO_VARIANTS.replace(
    'firstVariant:variants(first:1){edges{node{selectedOptions{name value}}}} ',
    'firstVariant:variants(first:1){edges{node{selectedOptions{name value}}}} ' +
    'variants(first:30){edges{node{id title price{amount} image{url} selectedOptions{name value}}}} '
  );

  // Alias retro-compat (le code existant utilise LIST_QUERY)
  var LIST_QUERY = LIST_QUERY_NO_VARIANTS;

  var PAGE_SIZE_LIST = 100;
  var MAX_PAGES = 5; // garde-fou : 500 produits max par collection

  function listProductsByCollection(handle, opts) {
    opts = opts || {};
    if (!handle) return Promise.resolve({ products: [], metafields: {} });

    // Cle de cache differenciee : "h" ou "h+v" selon includeVariants pour eviter
    // qu'un appel sans variants serve un appel avec variants ou vice-versa.
    var cacheKey = opts.includeVariants ? handle + '+v' : handle;
    if (!opts.bypassCache) {
      var cached = readListCache(cacheKey);
      if (cached) return Promise.resolve(cached);
    }

    if (_listInflight.has(cacheKey)) return _listInflight.get(cacheKey);

    var products = [];
    var metafields = {};
    var query = opts.includeVariants ? LIST_QUERY_WITH_VARIANTS : LIST_QUERY_NO_VARIANTS;

    var promise = (async function fetchAllPages() {
      var cursor = null;
      var page = 0;
      while (page < MAX_PAGES) {
        var variables = { h: handle, n: PAGE_SIZE_LIST, after: cursor };
        var data;
        try {
          data = await sfQuery(query, variables);
        } catch (err) {
          console.warn('[prix-conf] listProductsByCollection failed for', handle, ':', err.message);
          if (page === 0) throw err; // 1ere page fatale
          break;                      // sinon on retourne ce qu'on a deja
        }
        var col = data && data.collection;
        if (!col) break;
        var edges = (col.products && col.products.edges) || [];
        for (var i = 0; i < edges.length; i++) {
          var node = edges[i].node;
          var numericId = gidToNumeric(node.id);
          var featImg = node.featuredImage;
          // Premier variant (pour shim Etape B) : on conserve ses selectedOptions
          // pour permettre a l'adapter du configurateur de reconstruire un shim
          // qui passe le filtre buildSelectedOptions -> computeDimCaissonValue
          // (besoin de option<dimIdx+1> au format Shopify exact, qui differe
          // entre meuble-bas/haut/colonne et qu'on ne peut pas deviner du metafield).
          var fvEdges = (node.firstVariant && node.firstVariant.edges) || [];
          var firstVariantOpts = (fvEdges[0] && fvEdges[0].node && fvEdges[0].node.selectedOptions) || null;

          // Mode includeVariants : on map les variants Storefront vers la shape
          // products.json (option1/option2/option3 + featured_image) attendue
          // par le legacy renderCollection et getVariantByColor. Sinon variants
          // reste vide et la resolution se fait via prixConf a la demande.
          var renderVariants = [];
          if (opts.includeVariants) {
            var vEdges = (node.variants && node.variants.edges) || [];
            var allImages = []; // collecte pour images[] (utile a getVariantImage filename match)
            for (var vi = 0; vi < vEdges.length; vi++) {
              var vn = vEdges[vi].node;
              var sel = vn.selectedOptions || [];
              var vObj = {
                id: gidToNumeric(vn.id),
                title: vn.title || '',
                price: (vn.price && vn.price.amount) || '0',
                available: true,
                option1: (sel[0] && sel[0].value) || null,
                option2: (sel[1] && sel[1].value) || null,
                option3: (sel[2] && sel[2].value) || null,
              };
              if (vn.image && vn.image.url) {
                vObj.featured_image = { src: vn.image.url, id: gidToNumeric(vn.id), variant_ids: [vObj.id] };
                allImages.push({ src: vn.image.url, variant_ids: [vObj.id] });
              }
              renderVariants.push(vObj);
            }
            // Si featuredImage n'est pas deja dans les images variant, on l'ajoute
            // au debut pour la fallback getVariantImage (product.images[0]).
            if (featImg && !allImages.some(function(im) { return im.src === featImg.url; })) {
              allImages.unshift({ src: featImg.url });
            }
            var imagesOut = allImages.length ? allImages : (featImg ? [{ src: featImg.url }] : []);
          } else {
            var imagesOut = featImg ? [{ src: featImg.url }] : [];
          }

          products.push({
            id: numericId,
            _sfId: node.id,
            handle: node.handle,
            title: node.title,
            tags: node.tags || [],
            options: node.options || [],
            featured_image: featImg ? { src: featImg.url, alt: featImg.altText || '' } : null,
            images: imagesOut,
            variants: renderVariants, // [] si mode no-variants, sinon mappes
            priceRange: node.priceRange || null,
            _firstVariantOpts: firstVariantOpts // shim source pour Etape B (cf. configurateur-moood.js)
          });
          metafields[numericId] = {
            largeur_de_caisson: parseMetafieldValue(node.largeur && node.largeur.value),
            hauteur_de_caisson: parseMetafieldValue(node.hauteur && node.hauteur.value),
            profondeur_de_caisson: parseMetafieldValue(node.profondeur && node.profondeur.value),
            type_de_meuble: parseMetafieldValue(node.type_meuble && node.type_meuble.value),
            longueur_des_poignees: parseMetafieldValue(node.long_poig && node.long_poig.value),
            matiere_des_poignees: parseMetafieldValue(node.mat_poig && node.mat_poig.value),
            joue_et_fileur: parseMetafieldValue(node.joue && node.joue.value),
            type_d_electro: parseMetafieldValue(node.electro && node.electro.value),
            badge_custom: (node.badge && node.badge.value) || null
          };
        }
        var pi = col.products && col.products.pageInfo;
        if (!pi || !pi.hasNextPage) break;
        cursor = pi.endCursor;
        page += 1;
      }
      var result = { products: products, metafields: metafields };
      writeListCache(cacheKey, result);
      return result;
    })();

    _listInflight.set(cacheKey, promise);
    promise.finally(function() { _listInflight.delete(cacheKey); });
    return promise;
  }

  // ===== expose =================================================================
  window.mooodPrixConf = {
    prixConf: prixConf,
    prixConfBatch: prixConfBatch,
    listProductsByCollection: listProductsByCollection,
    _cache: _cache,
    _inflight: _inflight,
    _listCache: _listCache,
    _listInflight: _listInflight
  };
})();
/* touched 1780786100 - stepE LIST_QUERY_WITH_VARIANTS + opts.includeVariants */
